import runAccessibility from "@/views/menu/runAccessibility";
import { renderWidget } from "@/views/widget/widget";
import { registerKeyboardShortcut } from "@/utils/keyboardShortcut";
import { pluginConfig } from "@/globals/pluginConfig";
import { openMenu } from "@/views/menu/menu";
import { userSettings, getSavedUserSettings, saveUserSettings } from "@/globals/userSettings";
import { dropDisabledModuleStates } from "@/views/menu/moduleFilter";
import { changeLanguage } from "./i18n/changeLanguage";
import { getDefaultLanguage } from "./i18n/getDefaultLanguage";
import { loadLanguages } from "./i18n/Languages";

export interface AccessibilityWidget {
  changeLanguage: typeof changeLanguage;
}

/**
 * Shared runtime init (languages, saved settings, tool re-application).
 * Used by the plugin entry below and by the CDN core entry.
 */
export async function initRuntime(): Promise<void> {
  await loadLanguages();
  const savedSettings = getSavedUserSettings();
  // ALLY-141: a module disabled in config must reset any effect the visitor
  // previously enabled for it — drop those states before tools re-apply, and
  // persist the cleaned result immediately. Persisting is what makes the
  // reset deterministic: every widget interaction rewrites the whole settings
  // object anyway (saveUserSettings), so a stale key left in storage could
  // only ever survive until the visitor's next click — it never reliably
  // outlives a re-enable.
  const { states, changed } = dropDisabledModuleStates(
    savedSettings.states,
    pluginConfig.modules,
  );
  savedSettings.states = states;
  Object.assign(userSettings, savedSettings);
  if (changed) {
    saveUserSettings();
  }
  if (!userSettings.lang) {
    userSettings.lang = getDefaultLanguage();
  }
  runAccessibility();
}

export default async function accessibilityWidget(): Promise<AccessibilityWidget> {
  try {
    await initRuntime();
    renderWidget();
    registerKeyboardShortcut(pluginConfig.keyboard, openMenu);
    return { changeLanguage };
  } catch (error) {
    console.error("Failed to initialize accessibility widget:", error);
    return { changeLanguage };
  }
}